import React, { useEffect } from 'react';
import { useConsentStore } from '../store';
import { CookieBanner } from './CookieBanner';
import { CookieSettings } from './CookieSettings';
import { CookieConsentPreferences, CURRENT_CONSENT_VERSION } from '@/types';

// Extend window interface for embedded services
declare global {
  interface Window {
    ShellConsent?: {
      getPreferences: () => CookieConsentPreferences;
      onPreferenceChange: (callback: (prefs: CookieConsentPreferences) => void) => () => void;
    };
  }
}

interface ConsentProviderProps {
  children: React.ReactNode;
}

export const ConsentProvider: React.FC<ConsentProviderProps> = ({ children }) => {
  // Use store directly to get rehydration and persistence logic
  // We subscribe to preferences changes in the effect below, so we don't need to select it here for rendering
  const consentVersion = useConsentStore((state) => state.consentVersion);
  const resetConsent = useConsentStore((state) => state.resetConsent);

  // Check version on mount
  useEffect(() => {
    if (consentVersion !== CURRENT_CONSENT_VERSION) {
      resetConsent();
    }
  }, [consentVersion, resetConsent]);

  // Expose consent API to window for embedded services
  useEffect(() => {
    const listeners: Set<(prefs: CookieConsentPreferences) => void> = new Set();

    window.ShellConsent = {
      getPreferences: () => useConsentStore.getState().preferences,
      onPreferenceChange: (callback) => {
        listeners.add(callback);
        // Return unsubscribe function
        return () => {
          listeners.delete(callback);
        };
      },
    };

    // Notify listeners when preferences change
    const unsubscribe = useConsentStore.subscribe((state) => {
      listeners.forEach((callback) => callback(state.preferences));
      
      // Also post message for iframes
      const message = {
        type: 'SHELL_CONSENT_UPDATE',
        payload: state.preferences
      };
      
      // Broadcast to all iframes
      const iframes = document.querySelectorAll('iframe');
      iframes.forEach(iframe => {
        try {
          iframe.contentWindow?.postMessage(message, '*');
        } catch (e) {
          console.warn('Failed to post consent message to iframe', e);
        }
      });
    });

    return () => {
      delete window.ShellConsent;
      unsubscribe();
    };
  }, []);

  return (
    <>
      {children}
      <CookieBanner />
      <CookieSettings />
    </>
  );
};
